import { useState } from "react";
import { toast } from "sonner";
import { RateHistoryChart } from "@/components/RateHistoryChart";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, CURRENCIES, CURRENCY_OPTS } from "@/lib/api";
import { Panel, Table, Pill, useLoad, Loading, act } from "@/components/common";

function RateEditor({ fx, reload }) {
  const base = fx.reporting_currency;
  const cur = fx.current;
  const toBase = (c) => (cur.rates[c] && cur.rates[base] ? cur.rates[c] / cur.rates[base] : "");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rates, setRates] = useState(() => Object.fromEntries(Object.keys(CURRENCIES).map((c) => [c, c === base ? 1 : +Number(toBase(c)).toPrecision(6)])));
  const save = () => act(api.put("/fx/rates", { month, rates: Object.fromEntries(Object.entries(rates).map(([k, v]) => [k, v === "" ? null : v])) })).then((r) => {
    if (!r) return;
    const n = r.data.moved?.length || 0;
    toast.success(`Rates saved for ${month}${n ? ` — ${n} project${n > 1 ? "s" : ""} moved; PMO and PMs notified` : " — no converted totals changed"}`);
    reload();
  });
  return (
    <Panel title={`Set monthly rates — value of 1 unit in ${base}`} testid="fx-rate-editor">
      <div className="mb-3 flex items-center gap-2 text-sm">Month <input type="month" className="h-9 rounded-md border px-2" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="fx-month-input" /></div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Object.entries(CURRENCIES).map(([c, [s, n]]) => (
          <label key={c} className="text-sm"><span className="text-slate-500">1 {c} ({n}) =</span>
            <div className="flex items-center gap-1"><Input type="number" step="any" disabled={c === base} data-testid={`fx-rate-${c}`} value={rates[c]} onChange={(e) => setRates({ ...rates, [c]: e.target.value === "" ? "" : Number(e.target.value) })} /><span className="text-xs text-slate-500">{base}</span></div>
          </label>
        ))}
      </div>
      <Button className="mt-4" onClick={save} data-testid="fx-rates-save">Save rates for {month}</Button>
    </Panel>
  );
}

function CurrencySettings({ fx, reload }) {
  const [rep, setRep] = useState(fx.reporting_currency);
  const [def, setDef] = useState(fx.default_currency);
  const save = () => act(api.put("/fx/settings", { reporting_currency: rep, default_currency: def }), "Currency settings saved").then((r) => r && reload());
  const sel = (v, set, id) => <select className="h-9 rounded-md border px-2 text-sm" value={v} onChange={(e) => set(e.target.value)} data-testid={id}>{CURRENCY_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
  return (
    <Panel title="Currency settings" testid="fx-settings">
      <div className="flex flex-wrap items-end gap-4 text-sm">
        <label className="space-y-1"><div className="text-slate-500">Reporting currency (portfolio, finance & PDF totals)</div>{sel(rep, setRep, "fx-reporting-select")}</label>
        <label className="space-y-1"><div className="text-slate-500">Default for new projects</div>{sel(def, setDef, "fx-default-select")}</label>
        <Button variant="outline" onClick={save} data-testid="fx-settings-save">Save</Button>
      </div>
      <p className="mt-2 text-xs text-slate-500">After changing the reporting currency, enter a fresh set of rates against it.</p>
    </Panel>
  );
}

export function Currencies() {
  const { data: fx, error, reload } = useLoad(() => api.get("/fx").then((r) => r.data));
  if (!fx) return <Loading error={error} />;
  const cur = fx.current;
  return (
    <div className="space-y-4">
      {cur.sample && <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="fx-sample-warning">These are sample rates, not real ones. Finance should enter this month's rates before relying on converted totals.</div>}
      <Panel title={`Rates in use: ${cur.month || "sample"} (base ${cur.base})`} testid="fx-current">
        <Table rows={Object.keys(CURRENCIES).map((c) => ({ c, v: cur.rates[c] / cur.rates[fx.reporting_currency] }))} cols={[{ k: "c", h: "Currency", r: (x) => `${x.c} — ${CURRENCIES[x.c][1]}` },
          { k: "v", h: `1 unit in ${fx.reporting_currency}`, r: (x) => <span className="font-mono" data-testid={`fx-current-${x.c}`}>{Number(x.v).toPrecision(6)}</span> }]} />
        <p className="mt-2 text-xs text-slate-500">Conversions use the latest month on or before today.{cur.updated_by && ` Last set by ${cur.updated_by}.`}</p>
      </Panel>
      {fx.can_edit && <><CurrencySettings fx={fx} reload={reload} /><RateEditor key={`${fx.reporting_currency}-${cur.month}`} fx={fx} reload={reload} /></>}
      <RateHistoryChart history={fx.history} />
      <Panel title="Rate history" testid="fx-history">
        <Table rows={fx.history} empty="No rates entered yet" cols={[{ k: "month", h: "Month" }, { k: "base", h: "Base" }, { k: "updated_by", h: "Set by", r: (h) => h.updated_by || (h.sample ? <Pill tone="amber">sample</Pill> : "—") },
          { k: "updated_at", h: "When", r: (h) => (h.updated_at || "").slice(0, 16).replace("T", " ") }]} />
      </Panel>
    </div>
  );
}
